import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ComponentBaseComponent } from '@app/core/components/component-base/component-base.component';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { FileServerService } from '@app/core/services/file-server.service';
import type { FileDetails } from '@app/core/services/file-server.types';
import { hashFile } from '@app/core/services/file-server.utils';
import { AppSettingsService } from '@app/core/services/app-settings.service';
import { ModalService } from '@app/core/services/modal.service';
import { DocArcService } from '@app/services/doc-arc.service';
import { MergeDocument, PdfService } from '@app/services/pdf.service';
import { DocumentsMergedPreviewComponent } from './documents-merged-preview/documents-merged-preview.component';
import { DocumentsGridComponent } from '@app/shared/documents-grid/documents-grid.component';
import { DocumentItem, DocumentService, DocumentsGet, DocumentsParams } from '@app/services/document.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { Message, NullableOption, Recipient } from '@microsoft/microsoft-graph-types';

@GridModalComponent('documents')
@Component({
    selector: 'tt-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.css'],
    standalone: false,
})
export class DocumentsComponent extends ComponentBaseComponent implements OnInit, OnChanges {
    @Input()
    public params?: DocumentsParams;

    @Output()
    public getLoaded = new EventEmitter<DocumentsGet>();

    @ViewChild(DocumentsGridComponent)
    public documentsGrid?: DocumentsGridComponent;

    public get?: DocumentsGet;

    public filesToUpload: (File | Message)[] = [];

    public selectedDocumentType: 'd' | 'p' | 'v' = 'd';

    public searchtext = '';

    public uploading = false;

    public grid: GridOptions = new Grid()
        .setRememberId('w_documents.documents_grid')
        .setLoadDataFunction({ method: 2972, parameters: () => ({ ...this.params, doctype: this.selectedDocumentType, onlyFilenames: '0', searchtext: '' }) })
        .setCustomToolbarButtons([
            {
                name: 'merged_preview',
                text: 'merge_files_and_preview',
                translate: true,
                cssClass: 'tt-button tt-button--primary im-grid-btn-xs-l',
                disable_if_no_selected: true,
                func: async () => {
                    const docfilenames = this.grid.gridfunc!.getIsSelectedRows().map((row) => row['docfilename']);

                    const progress = this.modal.openProgressDialog({ value: 0, max: docfilenames.length, indeterminate: true, rememberId: 'w_documents.progress.merge' });

                    progress.updateIndeterminate(true);

                    let count = 0;

                    const fileArrayBuffers = await Promise.all(
                        docfilenames.map(async (label) => {
                            try {
                                const arraybuffer = await this.file.downloadFile({ fileName: label });
                                progress.updateValue((count = count + 1));

                                return arraybuffer;
                            } catch (error) {
                                progress.updateStatus('invalid');
                                progress.updateInvalidText(label);
                                console.error(error);
                            }

                            return null;
                        }),
                    );
                    try {
                        // The merger populates page bookkeeping for these content-only inputs.
                        const mergeDocuments = fileArrayBuffers.filter((arrayBuffer): arrayBuffer is ArrayBuffer => arrayBuffer !== null).map((arrayBuffer) => ({ content: arrayBuffer, contentType: 'application/pdf' }) as MergeDocument);

                        const pdf = await this.pdf.mergePDFs(mergeDocuments, { includeTableOfContents: false, fileName: this.getFileName() });
                        this.dialog.open(DocumentsMergedPreviewComponent, { data: { src: this.getUrl(pdf, 'documents_merged_pdf.pdf'), fileName: this.getFileName() } });
                        progress.updateIndeterminate(false);
                        progress.dialogRef.close();
                    } catch (error) {
                        console.error(error);
                    }
                },
            },
        ]);

    private getFileName(): string {
        return `${new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substring(0, 16).replaceAll('T', '_').replaceAll(':', '').replaceAll('-', '')}_documents_${Object.entries(this.navigator?.params ?? this.params ?? {})
            .filter((val) => val[0] !== '#')
            .reduce((acc, val) => acc + `${val[1]}_`, '')}merged_documents.pdf`;
    }

    private getUrl(base64Data: string, filename: string) {
        const base64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

        const byteCharacters = atob(base64);
        const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
        const byteArray = new Uint8Array(byteNumbers);

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const file = new File([blob], filename, { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(file);

        return blobUrl;
    }

    public documents: DocumentItem[] = [];

    constructor(
        private document: DocumentService,
        private docarc: DocArcService,
        private file: FileServerService,
        private pdf: PdfService,
        private modal: ModalService,
        private dialog: MatDialog,
        private appSettings: AppSettingsService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        super();

        this.navigator = useNavigatorFor(navigator, this);
    }

    public async uploadFiles() {
        if (this.uploading || this.filesToUpload.length === 0 || !this.params || !this.get) return;
        const files: File[] = this.filesToUpload.filter((item): item is File => item instanceof File);
        const messages: Message[] = this.filesToUpload.filter((item): item is Message => !(item instanceof File));

        this.uploading = true;
        const progress = this.modal.openProgressDialog({ indeterminate: true, max: files.length + messages.length, value: 0, disableClose: true, label: 'preparing_files', translateLabel: true });

        try {
            await this.uploadDocumentFiles(files, progress);

            progress.setLabel('uploading_files', true);
            progress.updateIndeterminate(false);
            let completed = files.length;
            for (let message of messages) {
                await this.docarc.saveEmailItem({
                    heading: this.base64UrlEncodeUnicode(message.subject ?? ''),
                    email_body_html: this.base64UrlEncodeUnicode(message.body?.content ?? ''),
                    recipient_email: this.getSeperatedListOfAddresses(message.toRecipients),
                    datetime_sent: message.sentDateTime ?? '',
                    datetime_received: message.receivedDateTime ?? '',
                    sentDateTimeZulu: message.sentDateTime ?? '',
                    sender_email: message.sender?.emailAddress?.address ?? '',
                    sender_name: message.sender?.emailAddress?.name ?? '',
                    email_cc: this.getSeperatedListOfAddresses(message.ccRecipients),
                    email_bcc: this.getSeperatedListOfAddresses(message.bccRecipients),
                    attachments: message.attachments ?? [],
                    argtype: this.get.argtype,
                    argvalue: this.get.argvalue,
                });
                progress.updateValue(++completed);
            }

            this.filesToUpload = [];
            progress.updateStatus('finish');
        } catch (error) {
            console.error(error);
            progress.updateIndeterminate(false);
            progress.updateStatus('invalid');
            progress.updateInvalidText(error instanceof Error ? error.message : `${error}`);
        } finally {
            progress.data.disableClose = false;
            progress.dialogRef.disableClose = false;
            this.uploading = false;
            await this.read();
        }
    }

    private getSeperatedListOfAddresses(recipients?: NullableOption<Recipient[]>, seperator: string = ','): string {
        return (recipients ?? [])
            .map((recipient) => recipient.emailAddress?.address)
            .filter((address) => !!address)
            .join(seperator);
    }

    private async uploadDocumentFiles(files: File[], progress: ReturnType<ModalService['openProgressDialog']>): Promise<void> {
        if (!this.params) return;
        let completed = 0;
        for (const file of files) {
            progress.setLabel('preparing_files', true);
            progress.updateIndeterminate(true);
            const checksum_md5 = await hashFile(file);
            const { exists } = await this.docarc.checkFileExists({ filename: file.name, checksum_md5, argtype: this.params.argtype, argvalue: this.params.argvalue });

            progress.setLabel('uploading_files', true);
            progress.updateIndeterminate(false);
            if (!exists) {
                const uploadedFile = await this.file.uploadFile({ file });
                if ((uploadedFile.fileItemKeyno ?? 0) > 0) await this.addFileToDocarc({ ...uploadedFile, checksum_md5 });
            }
            progress.updateValue(++completed);
        }
    }

    private addFileToDocarc(data: FileDetails & { checksum_md5: string }) {
        if (!this.params) return;

        const docarc = {
            contentType: data.contentType || '',
            filename: data.fileName || '',
            localFileName: data.localFileName || '', // BJS 20211019
            // originalFileName: data.originalFileName || '',
            originalTimestamp: data.originalDateTime || '',
            argType: this.params.argtype || '',
            argValue: this.params.argvalue || '',
            size: data.size || 0,
            checksum: data.checksum || '',
            latitude: data.latitude || 0,
            longitude: data.longitude || 0,
            altitude: data.altitude || 0,
            heading: data.heading || 0,
            checksum_md5: data.checksum_md5 || 0,
        };

        if (!!docarc.filename?.trim()) {
            return this.docarc.add(docarc);
        } else {
            throw Error('Filename is required');
        }
    }

    public async read() {
        await this.loadDocuments();
        this.documentsGrid?.grid?.gridfunc?.read();
        this.grid.gridfunc?.read();
    }

    public async openSettings(event: MouseEvent) {
        event.preventDefault();

        await this.modal.openStateDialog({ state: 'webpagesettingmenu', params: 'documents' }, { width: '90vw', height: '80vh' });
        await this.loadGet();
        await this.read();
    }

    private async loadGet() {
        if (!this.params) return;
        this.get = await this.document.getDocuments(this.params);
        this.getLoaded.emit(this.get);
        this.navigator?.setHeading?.(this.get?.page_heading ?? '');
    }

    private async loadDocuments() {
        if (!!this.params && !!this.selectedDocumentType) {
            this.documents = await this.document.getDocumentsList({ ...this.params, max_nbr_of_rows: 5000, searchtext: '', doctype: this.selectedDocumentType, onlyFilenames: '0', glyphicon_select: '1' });
        }
    }

    private async init() {
        await this.appSettings.ensureIsReady();
        await this.loadGet();
        await this.read();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
    public async ngOnInit(): Promise<void> {
        const params = this.navigator?.params;
        if (this.navigator && params && 'argtype' in params && 'argvalue' in params) {
            if (this.navigator.host === 'dialog') this.navigator.setSize?.({ height: '100vh', width: '100vw' });
            this.params = params as unknown as DocumentsParams;
            await this.init();
        }
    }
}
